// Phase JB.2 — Trigger surface.
//
// Renders the latest UN-DISMISSED trigger_evaluations row using
// I0 primitives only (Eyebrow + Pill + Stat). Hidden if no trigger.
// CTA varies by rule_id and routes through TriggerPrompt's
// `onAction` callback so the parent owns navigation/start-trial.

import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Pill } from "../super-acca";
import { useTrigger } from "../../conversion/useTrigger.ts";
import { useTrial } from "../../conversion/useTrial.ts";
import type { TriggerEvaluation } from "../../conversion/useTrigger.ts";

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";

const wrapperStyle: CSSProperties = { padding: "8px 48px 8px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 18,
  margin: "0 auto",
  maxWidth: 1180,
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

interface RuleCopy {
  headline: string;
  ctaLabel: string;
  ctaAction: "start_trial" | "view_gap_report" | "view_pricing";
}

const RULE_COPY: Record<string, RuleCopy> = {
  gap_threshold: {
    headline: "Your gap report shows real foregone returns this week",
    ctaLabel: "START 7-DAY TRIAL",
    ctaAction: "start_trial",
  },
  streak_break: {
    headline: "One loss after a win streak — the intelligence still holds",
    ctaLabel: "SEE YOUR GAP REPORT",
    ctaAction: "view_gap_report",
  },
  pro_signal_density: {
    headline: "You hovered Pro signals 10+ times this week",
    ctaLabel: "START 7-DAY TRIAL",
    ctaAction: "start_trial",
  },
  slip_velocity: {
    headline: "You are using STRIKZA seriously — the trial unlocks the rest",
    ctaLabel: "START 7-DAY TRIAL",
    ctaAction: "start_trial",
  },
  consecutive_loss_recovery: {
    headline: "Two losses. Reset your read with a 7-day Pro trial — no card required.",
    ctaLabel: "START 7-DAY TRIAL · NO CARD",
    ctaAction: "start_trial",
  },
  paywall_seen_density: {
    headline: "You have looked at Pro three times — try it for a week",
    ctaLabel: "START 7-DAY TRIAL",
    ctaAction: "start_trial",
  },
};

export interface TriggerPromptProps {
  /** Test override: pass a trigger row. */
  triggerOverride?: TriggerEvaluation | null;
  /** Test override: bypass dismiss. */
  onDismissOverride?: () => void;
}

export function TriggerPrompt({
  triggerOverride,
  onDismissOverride,
}: TriggerPromptProps = {}): JSX.Element | null {
  const { trigger: liveTrigger, dismiss } = useTrigger();
  const { startTrial, trial } = useTrial();
  const trigger = triggerOverride === undefined ? liveTrigger : triggerOverride;

  if (!trigger) return null;
  // Active trial → no trigger pump needed.
  if (trial && trial.status === "active") return null;

  const copy = RULE_COPY[trigger.rule_id];
  if (!copy) return null;

  const handleDismiss = (): void => {
    if (onDismissOverride) onDismissOverride();
    else void dismiss();
  };

  const ctaInner = (
    <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
      {copy.ctaLabel}
    </Pill>
  );

  return (
    <section
      aria-label="Conversion trigger"
      data-testid="trigger-prompt"
      data-rule-id={trigger.rule_id}
      style={wrapperStyle}
    >
      <div style={cardStyle}>
        <span
          style={{
            fontFamily: fontMono,
            fontSize: 10.5,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-muted)",
          }}
        >
          STRIKZA · MOMENT
        </span>
        <div
          style={{
            fontFamily: fontBody,
            fontSize: 14,
            color: "var(--sa-fg-primary)",
            flex: 1,
            minWidth: 240,
          }}
        >
          <Eyebrow>{trigger.rule_id.replace(/_/g, " ").toUpperCase()}</Eyebrow>
          <div style={{ marginTop: 4 }}>{copy.headline}</div>
        </div>
        {copy.ctaAction === "start_trial" ? (
          <button
            type="button"
            onClick={() => void startTrial("trigger")}
            data-testid="trigger-prompt-cta"
            style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
          >
            {ctaInner}
          </button>
        ) : copy.ctaAction === "view_gap_report" ? (
          <Link to="/dashboard" data-testid="trigger-prompt-cta" style={{ textDecoration: "none" }}>
            {ctaInner}
          </Link>
        ) : (
          <Link to="/pricing" data-testid="trigger-prompt-cta" style={{ textDecoration: "none" }}>
            {ctaInner}
          </Link>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="trigger-prompt-dismiss"
          style={{
            background: "transparent",
            border: 0,
            padding: "4px 8px",
            fontFamily: fontMono,
            fontSize: 10.5,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-muted)",
            cursor: "pointer",
          }}
        >
          DISMISS
        </button>
      </div>
    </section>
  );
}
